import React from 'react'

import EmptyState from '@/components/base/emptyState'

import { Users } from 'lucide-react'
import { Author } from '@/domain'
import { cn } from '@/lib/utils'

import { AppContext } from '@/components/base/appcontext'
import CarouselRow, { CAROUSEL_CARD } from '@/components/base/carouselRow'
import CoachBadge from '@/components/base/coachBadge'
import LinkWithState from '@/components/base/link'
import UserAvatar from '@/components/base/userAvatar'

import { Typography } from '@/components/ui/typography'

export interface CoachPreview extends Author {
    pricePerCustomLesson?: number
}

const CoachesCarousel: React.FC<{
    items: CoachPreview[]
    seeMoreLink?: string
    // Extra list items rendered after the coaches, before "see more".
    trailing?: React.ReactNode
}> = ({ items, seeMoreLink, trailing }) => {
    const appContext = React.useContext(AppContext)

    return (
        <CarouselRow
            empty={items.length === 0 && !trailing}
            emptyState={
                <EmptyState
                    variant="section"
                    icon={Users}
                    title={appContext.geti18n('homepage.topCoaches.noItems')}
                    description={appContext.geti18n(
                        'empty.topCoaches.description'
                    )}
                    action={{
                        label: appContext.geti18n('empty.coaches.become'),
                        to: '/coach/apply'
                    }}
                />
            }
            seeMoreLink={seeMoreLink}
        >
            {items.map((coach) => (
                <li
                    key={'coach-' + coach.id}
                    className={cn(CAROUSEL_CARD, 'border bg-card')}
                >
                    <LinkWithState
                        to={`/coaches/${coach.id}`}
                        className="flex items-start gap-2.5 p-3"
                    >
                        <UserAvatar
                            name={coach.login}
                            photo={coach.photo}
                            coach
                            className="h-9 w-9 shrink-0"
                            fallbackClassName="text-xs font-semibold"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                                <Typography
                                    variant="title-sm"
                                    className="leading-tight truncate"
                                >
                                    {coach.login}
                                </Typography>
                                <CoachBadge>{coach.level}</CoachBadge>
                            </div>
                            <Typography
                                variant="body-xs"
                                className="leading-tight truncate mb-1"
                            >
                                {coach.bio}
                            </Typography>
                            {!!coach.pricePerCustomLesson && (
                                <Typography
                                    variant="body-xs"
                                    className="text-muted-foreground"
                                >
                                    {appContext.geti18n(
                                        'homepage.topCoaches.priceFrom'
                                    )}{' '}
                                    {coach.pricePerCustomLesson}⭐
                                </Typography>
                            )}
                        </div>
                    </LinkWithState>
                </li>
            ))}
            {trailing}
        </CarouselRow>
    )
}

export default CoachesCarousel
